/*	Copyright (c) 1996 - 2026 NoWatt Ltd				*/
/*	  All Rights Reserved  											*/

/*	THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF NoWatt Ltd	*/
/*	The copyright notice above does not evidence any   				*/
/*	actual or intended publication of such source code.				*/

'use strict';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';

interface DateComponentProps {
  fromx: moment.Moment;
  tox: moment.Moment;
  update: (from: moment.Moment, to: moment.Moment) => void;
}

const PRESETS: [string, () => moment.Moment, () => moment.Moment][] = [
  ['Today',       () => moment().startOf('day'),                           () => moment().startOf('day').add(1, 'days')],
  ['Yesterday',   () => moment().subtract(1, 'days').startOf('day'),       () => moment().startOf('day')],
  ['Last 7 Days', () => moment().subtract(7, 'days'),                      () => moment().startOf('day')],
  ['This Month',  () => moment().startOf('month'),                         () => moment().endOf('month')],
  ['Last Month',  () => moment().subtract(1, 'month').startOf('month'),    () => moment().subtract(1, 'month').endOf('month')]
];

const detectPreset = (start: moment.Moment, end: moment.Moment) => {
  for(const [name, fromFn, toFn] of PRESETS) {
    if(Math.abs(start.diff(fromFn())) < 60000 && Math.abs(end.diff(toFn())) < 60000)
      return name;
  }
  return 'Custom';
}

const DateComponent = ({ fromx, tox, update }: DateComponentProps) => {
  const [open, setOpen] = useState(false);
  const [tempStart, setTempStart] = useState(fromx.clone());
  const [tempEnd, setTempEnd] = useState(tox.clone());
  const [activePreset, setActivePreset] = useState(() => detectPreset(fromx, tox));
  const [viewMonth, setViewMonth] = useState(() => fromx.clone().startOf('month'));
  const [hoverDay, setHoverDay] = useState<moment.Moment | null>(null);
  const [clickCount, setClickCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if(containerRef.current && !containerRef.current.contains(e.target as Node))
      setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [handleOutsideClick]);

  const toggle = () => {
    const newStart = fromx.clone();
    const newEnd = tox.clone();
    setTempStart(newStart);
    setTempEnd(newEnd);
    setActivePreset(detectPreset(newStart, newEnd));
    setViewMonth(newStart.clone().startOf('month'));
    setHoverDay(null);
    setClickCount(0);
    setOpen(o => !o);
  }

  const selectPreset = (fromFn: () => moment.Moment, toFn: () => moment.Moment, name: string) => {
    update(fromFn(), toFn());
    setOpen(false);
    setActivePreset(name);
  }

  const apply = () => {
    update(tempStart, tempEnd);
    setOpen(false);
  }

  const cancel = () => {
    setOpen(false);
  }

  const handleDayClick = (day: moment.Moment) => {
    if(clickCount === 0) {
      const newStart = day.clone().hour(tempStart.hour()).minute(tempStart.minute()).second(0);
      setTempStart(newStart);
      setTempEnd(newStart.clone().hour(tempEnd.hour()).minute(tempEnd.minute()));
      setClickCount(1);
    } else {
      let end = day.clone().hour(tempEnd.hour()).minute(tempEnd.minute()).second(0);
      let start = tempStart;
      if(end.isBefore(start, 'day')) { const tmp = start; start = end; end = tmp; }
      setTempStart(start);
      setTempEnd(end);
      setClickCount(0);
      setHoverDay(null);
      setActivePreset('Custom');
    }
  }

  const handleDayHover = (day: moment.Moment) => {
    if(clickCount === 1) setHoverDay(day);
  }

  const setTime = (which: 'start' | 'end', field: 'hour' | 'minute', val: number) => {
    const apply = (m: moment.Moment) => field === 'hour' ? m.clone().hour(val) : m.clone().minute(val);
    if(which === 'start') setTempStart(apply(tempStart));
    else setTempEnd(apply(tempEnd));
  }

  const renderMonth = (monthMoment: moment.Moment) => {
    let dispStart = tempStart, dispEnd = tempEnd;
    if(clickCount === 1 && hoverDay) {
      dispStart = hoverDay.isBefore(tempStart, 'day') ? hoverDay : tempStart;
      dispEnd   = hoverDay.isBefore(tempStart, 'day') ? tempStart : hoverDay;
    }
    const firstDay = monthMoment.clone().startOf('month');
    const lastDay  = monthMoment.clone().endOf('month');
    const gridStart = firstDay.clone().startOf('week');
    const gridEnd   = lastDay.clone().endOf('week');
    const days: moment.Moment[] = [];
    for(let d = gridStart.clone(); !d.isAfter(gridEnd, 'day'); d.add(1, 'day')) days.push(d.clone());
    const weeks: moment.Moment[][] = [];
    for(let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    const DOW = ['Su','Mo','Tu','We','Th','Fr','Sa'];
    return (
      <div style={{padding:'4px 10px'}}>
        <div style={{textAlign:'center', fontWeight:'bold', fontSize:'13px', marginBottom:'6px'}}>
          {monthMoment.format('MMMM YYYY')}
        </div>
        <table style={{width:'100%', borderCollapse:'collapse', fontSize:'12px'}}>
          <thead>
            <tr>{DOW.map(d => <th key={d} style={{textAlign:'center', padding:'2px 3px', color:'#999', fontWeight:'normal'}}>{d}</th>)}</tr>
          </thead>
          <tbody>
            {weeks.map((week, wi) => (
              <tr key={wi}>
                {week.map((day, di) => {
                  const inMonth  = day.month() === monthMoment.month();
                  const isStart  = day.isSame(dispStart, 'day');
                  const isEnd    = day.isSame(dispEnd, 'day');
                  const inRange  = day.isBetween(dispStart, dispEnd, 'day', '[]');
                  const endpoint = isStart || isEnd;
                  const cellBg   = endpoint ? '#357ebd' : (inRange ? '#ebf4f8' : 'transparent');
                  const cellClr  = endpoint ? '#fff' : (inRange ? '#08c' : (inMonth ? '#333' : '#ccc'));
                  return (
                    <td key={di} style={{textAlign:'center', padding:'1px'}}
                      onMouseEnter={() => handleDayHover(day)}>
                      <div onClick={() => inMonth && handleDayClick(day)}
                        style={{
                          cursor: inMonth ? 'pointer' : 'default',
                          background: cellBg, color: cellClr,
                          borderRadius: '4px', fontWeight: endpoint ? 'bold' : 'normal',
                          padding:'3px 5px', display:'inline-block', minWidth:'24px'
                        }}>
                        {day.date()}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const renderTimePicker = (which: 'start' | 'end') => {
    const m = which === 'start' ? tempStart : tempEnd;
    const label = which === 'start' ? 'From:' : 'To:';
    const hrs  = Array.from({length:24}, (_, i) => i);
    const mins = Array.from({length:60}, (_, i) => i);
    return (
      <div style={{display:'flex', alignItems:'center', gap:'4px', padding:'4px 10px',
        borderBottom:'1px solid #eee', fontSize:'12px', color:'#555'}}>
        <span style={{fontWeight:'bold', minWidth:'32px'}}>{label}</span>
        <span style={{flex:1}}>{m.format('ll')}</span>
        <i className="bi bi-clock" style={{fontSize:'11px'}}></i>
        <select value={m.hour()} onChange={e => setTime(which,'hour',+e.target.value)}
          style={{width:'46px', fontSize:'12px', padding:'1px'}}>
          {hrs.map(h => <option key={h} value={h}>{String(h).padStart(2,'0')}</option>)}
        </select>
        <span>:</span>
        <select value={m.minute()} onChange={e => setTime(which,'minute',+e.target.value)}
          style={{width:'46px', fontSize:'12px', padding:'1px'}}>
          {mins.map(v => <option key={v} value={v}>{String(v).padStart(2,'0')}</option>)}
        </select>
      </div>
    );
  }

  const start = fromx.format('ll HH:mm');
  const end   = tox.format('ll HH:mm');
  const label = (start === end ? start : start + ' - ' + end) + ' ';
  const isCustom = activePreset === 'Custom';
  const leftMonth  = viewMonth.clone();
  const rightMonth = viewMonth.clone().add(1, 'month');

  const presetStyle = (name: string) => ({
    display: 'block', fontSize: '13px', whiteSpace: 'nowrap',
    background: activePreset === name ? '#08c' : '#f5f5f5',
    border: activePreset === name ? '1px solid #08c' : '1px solid #f5f5f5',
    color: activePreset === name ? '#fff' : '#08c',
    padding: '3px 12px', marginBottom: '8px', borderRadius: '4px', cursor: 'pointer'
  });

  return (
    <div ref={containerRef} style={{position:'relative', display:'inline-block', width:'100%'}}>
      <button className="selected-date-range-btn btn btn-secondary" style={{width:'100%'}} onClick={toggle}>
        <i className="bi bi-calendar"></i>
        <span>{label}</span>
      </button>
      {open && (
        <div style={{
          position:'absolute', zIndex:1000, left:0, top:'100%', marginTop:'1px',
          background:'#fff', border:'1px solid #ccc', borderRadius:'4px',
          boxShadow:'0 2px 5px rgba(0,0,0,0.2)', display:'flex', userSelect:'none'
        }}>
          {/* Preset sidebar */}
          <div style={{borderRight:'1px solid #ddd', padding:'4px', flex:'0 0 auto',
            display:'flex', flexDirection:'column', justifyContent:'space-between'}}>
            <div>
              {PRESETS.map(([name, fromFn, toFn]) => (
                <div key={name} className="drp-range-item" style={presetStyle(name)}
                  onClick={() => selectPreset(fromFn, toFn, name)}>
                  {name}
                </div>
              ))}
              <div className="drp-range-item" style={{...presetStyle('Custom'), marginBottom:0}}
                onClick={() => setActivePreset('Custom')}>
                Custom Range
              </div>
            </div>
            {isCustom && (
              <div style={{marginTop:'12px'}}>
                <button className="btn btn-sm btn-success" style={{marginRight:'4px'}} onClick={apply}>Apply</button>
                <button className="btn btn-sm btn-secondary" onClick={cancel}>Cancel</button>
              </div>
            )}
          </div>
          {/* Inline calendar — only shown for Custom Range */}
          {isCustom && (
            <div>
              {renderTimePicker('start')}
              {renderTimePicker('end')}
              {clickCount === 1 && (
                <div style={{textAlign:'center', fontSize:'11px', color:'#888', padding:'2px 0',
                  background:'#fffae6', borderBottom:'1px solid #eee'}}>
                  Now click the end date
                </div>
              )}
              <div style={{display:'flex', alignItems:'flex-start'}}>
                <button className="btn btn-sm btn-secondary"
                  style={{margin:'8px 2px 0 4px', alignSelf:'flex-start'}}
                  onClick={() => setViewMonth(viewMonth.clone().subtract(1,'month'))}>
                  ‹
                </button>
                <div style={{display:'flex'}}>
                  {renderMonth(leftMonth)}
                  <div style={{width:'1px', background:'#eee', margin:'4px 0'}} />
                  {renderMonth(rightMonth)}
                </div>
                <button className="btn btn-sm btn-secondary"
                  style={{margin:'8px 4px 0 2px', alignSelf:'flex-start'}}
                  onClick={() => setViewMonth(viewMonth.clone().add(1,'month'))}>
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DateComponent;
